import { z } from "zod";
import type {
  EntityId,
  PlaceholderRef,
  RelationOp,
} from "@/lib/core/schemas/relations";
import { PlaceholderRefSchema } from "@/lib/core/schemas/relations";
import { EntityPatchOpSchema } from "@/lib/kernel/op-pipeline/patch-schema";

/**
 * The op shape for creating an entity instance through the op pipeline.
 *
 * NEW in LAF-91 Extension A:
 *   - `placeholder?: string` — an optional symbol bound to the new entity ID.
 *     Other ops in the same array can reference the symbol via PlaceholderRef
 *     ({ $: "<name>" }) and the apply phase resolves it to the bound ID.
 *   - `containedIn` and `locatedIn` accept `EntityId | PlaceholderRef | null`,
 *     so an entity-create op can reference a placeholder bound by an earlier
 *     op in the same array.
 *
 * Existing call sites (e.g., the spawn_entity_instance tool) continue to
 * pass concrete EntityIds and omit `placeholder`. The placeholder mechanism
 * is opt-in per op.
 */
export type EntityCreateOp = {
  readonly op: "create-entity";
  readonly placeholder?: string;
  /**
   * Optional caller-supplied entity ID. When provided, the op-pipeline uses
   * this value instead of generating a ULID via `ctx.newId()`. Intended for
   * testing and deterministic simulation scenarios where a stable ID must be
   * known ahead of time (e.g. `forge_scenario`/`forge_objective` callers that
   * need to reference the new entity in subsequent steps). Production paths
   * should omit this and let the pipeline allocate a ULID.
   */
  readonly entityId?: string;
  readonly definitionId: string;
  readonly instanceName: string;
  /**
   * Optional free-text description override. When present, the op pipeline
   * prefers it over the resolved definition's `description` when
   * constructing the created EntityInstance (apply-entity-create.ts).
   * Additive/optional — existing op-array producers that omit this field
   * are unaffected. `forge_location` is the first consumer (threads the
   * narrator's forge-entry `description` so it is not silently dropped).
   */
  readonly description?: string;
  readonly containedIn: EntityId | PlaceholderRef | null;
  readonly locatedIn: EntityId | PlaceholderRef | null;
  /** Free-form per-definition state overrides (resources, scenarioSide, stats, etc.) */
  readonly state?: Record<string, unknown>;
  /**
   * Optional control mode for the created entity. Defaults to "none" (mirrors
   * the prior hardcode). `forge_pc_5e` sets "human" — a forged PC is a player
   * character, so scenario templates (which resolve the PC by control==="human")
   * can find it. spawn_entity_instance uses its own direct-write path, unaffected.
   */
  readonly control?: "human" | "ai" | "none";
  /**
   * Optional owner user ID stamped on the created entity. `forge_pc_5e`
   * stamps `context.session.userId` on player-character creates so
   * `isOwner`/`canControlActor` can enforce real per-player ownership
   * (retiring the `soleHuman` shim — see lib/core/utils/auth.ts). Absent
   * for NPC/monster/item forges.
   */
  readonly ownerId?: string;
};

/**
 * The op shape for destroying an entity instance through the op pipeline.
 *
 * Delegates to the canonical `destroyEntity` helper (lib/kernel/relations/
 * destroy-entity.ts): orphans spatial children, runs per-kind relation
 * lifecycle (orphan/cascade/persist) over involved entity_relations rows,
 * and removes the entity from `state.entities`. `entityId` must reference an
 * entity already present in live `state.entities` at call entry — the
 * placeholder-ref form is accepted for shape-consistency with the create/
 * relation ops (resolved against the same `PlaceholderBindings`), but a
 * placeholder bound by a *same-array* create will NOT resolve to a
 * destroyable entity: creates stage into the COW overlay and only merge to
 * live state at commit (Phase 6), after destroys run (Phase 4d), so a
 * same-array create-then-destroy throws "not found". `reason` is free-form
 * teardown intent, threaded into every cascade relation-delete's `intent`
 * field (the closed `RelationOpApplied.reason` enum can't hold it).
 *
 * Not part of `KernelOp` (the topo-sorted create+relation array): destroy
 * ops carry no placeholder *binding* (only an optional reference) and run
 * LAST in the apply phase (Phase 4d), after relation writes, so the relation
 * graph is still live when the per-kind cascade fires. They therefore do not
 * participate in the create→relation ordering graph.
 */
export type EntityDestroyOp = {
  readonly op: "destroy-entity";
  readonly entityId: EntityId | { readonly $: string };
  readonly reason?: string;
};

/** Combined op-array element type. The op pipeline accepts both backends. */
export type KernelOp = EntityCreateOp | RelationOp;

/**
 * Zod schema for EntityDestroyOp. The `as z.ZodType<EntityDestroyOp>` cast
 * is needed for the same branded-type reason as EntityCreateOpSchema below.
 */
export const EntityDestroyOpSchema: z.ZodType<EntityDestroyOp> = z.object({
  op: z.literal("destroy-entity"),
  // PlaceholderRefSchema (.strict().min(1)) is stricter than the inlined
  // { $: z.string() } the create/patch siblings use — rejects empty/extra-key
  // refs. Free to harden here since the schema is already imported.
  entityId: z.union([z.string(), PlaceholderRefSchema]),
  reason: z.string().optional(),
}) as z.ZodType<EntityDestroyOp>;

/**
 * Zod schema for EntityCreateOp. Owned logically by LAF-91 (entity-create
 * op shape is part of the LAF-12 extension surface); LAF-99 introduces the
 * schema here for V1 phase-1 schema validation. Future LAF-91 work may
 * evolve the shape; the schema and the type stay co-located.
 *
 * The `as z.ZodType<EntityCreateOp>` cast is needed because EntityCreateOp
 * uses branded types for EntityId / PlaceholderRef that the zod runtime
 * can't enforce at parse time. The cast is from the schema's inferred
 * shape (which is a strict superset of the type) to the branded-type
 * interface — internal type narrowing, not from an untrusted source.
 */
export const EntityCreateOpSchema: z.ZodType<EntityCreateOp> = z.object({
  op: z.literal("create-entity"),
  placeholder: z.string().optional(),
  entityId: z.string().optional(),
  definitionId: z.string(),
  instanceName: z.string(),
  description: z.string().optional(),
  containedIn: z.union([z.string(), z.object({ $: z.string() }), z.null()]),
  locatedIn: z.union([z.string(), z.object({ $: z.string() }), z.null()]),
  state: z.record(z.unknown()).optional(),
  control: z.enum(["human", "ai", "none"]).optional(),
  ownerId: z.string().optional(),
}) as z.ZodType<EntityCreateOp>;

/**
 * Relation create op for use inside an `OpArrayTemplate`.
 * Only the create variant is supported in templates — update/delete ops
 * are never pre-declared in an ExecutionPlan; they execute imperatively.
 *
 * Endpoints may be a concrete entity ID (string) or a
 * `{$: placeholder}` ref that `ActionSystem.applyResolvedOps` resolves
 * against the placeholder→ID map built from `entityCreateOps` in the
 * same template.
 */
export const RelationCreateOpSchema = z.object({
  op: z.literal("create"),
  kind: z.string().min(1),
  endpointA: z.union([z.string().min(1), PlaceholderRefSchema]),
  endpointB: z.union([z.string().min(1), PlaceholderRefSchema]),
  attrs: z.unknown(), // validated against the kind's attrsSchema in the apply phase
});

/**
 * An op-array fragment carried by an `ExecutionPlan` (the LAF-251 op channel).
 *
 * Consumed by `ActionSystem.applyResolvedOps` either once at cast-time
 * (`setupOps`) or once per matched outcome (`opsByOutcome[id]`). Placeholders
 * bound by `entityCreateOps` are resolved before relation ops are applied.
 */
export const OpArrayTemplateSchema = z.object({
  entityCreateOps: z.array(EntityCreateOpSchema).default([]),
  // Additive, optional patch-op channel (spell-anchor slice 2): carries
  // compose-profile / decompose-profile (and any other EntityPatchOp) so an
  // action's plan can drive in-tx component/profile mutations alongside its
  // entity-create + relation ops. `.optional()` (not `.default([])`) keeps the
  // inferred OpArrayTemplate's `entityPatchOps` non-required, so existing
  // producers that omit it are unaffected; the consumer reads it as `?? []`.
  entityPatchOps: z.array(EntityPatchOpSchema).optional(),
  relationOps: z.array(RelationCreateOpSchema).default([]),
  // Destroy channel (Dispel Magic slice 1): carries destroy-entity ops so a
  // plan's per-outcome fragment can tear down an entity (e.g. a concentration
  // anchor) at apply time. `.optional()` (not `.default([])`) keeps the inferred
  // OpArrayTemplate's `entityDestroyOps` non-required, so existing producers that
  // omit it are unaffected; the consumer reads it as `?? []`. applyOpArray
  // already accepts + applies an `entityDestroyOps` array (Phase 4d).
  entityDestroyOps: z.array(EntityDestroyOpSchema).optional(),
});
export type OpArrayTemplate = z.infer<typeof OpArrayTemplateSchema>;

/**
 * Component-attrs patch op — shallow-merges a set of attrs into an existing
 * `entity_components` slot keyed by `(entityId, componentId)`.
 *
 * Added in LAF-94a PR2 (gemini Comment 1 substrate amendment). Designed as
 * a standalone op consumed by the conditions cluster's duplicate-apply path,
 * and generally useful for equipment, character building, and any other system
 * that needs atomic in-tx component-attrs patches within the op-pipeline.
 *
 * This op is NOT part of `KernelOp` (the topo-sort combined array of creates
 * + relations). Callers translate it to a path-based `merge` `EntityPatchOp`
 * via `entityComponentPatchToMergeOp()` before passing to `applyOpArray`'s
 * `entityPatchOps` array. The separation preserves the existing topo-sort
 * boundary: component-attrs patches have no placeholder dependencies, so they
 * do not participate in the create→relation ordering graph.
 *
 * `attrs` is shallow-merged: existing keys not present in `attrs` are
 * preserved. Caller controls deep semantics by passing nested objects.
 */
export type EntityComponentPatchOp = {
  readonly op: "patch-entity-component";
  readonly entityId: EntityId | PlaceholderRef;
  readonly componentId: string;
  /** Shallow-merged into existing component attrs. Caller controls deep semantics. */
  readonly attrs: Readonly<Record<string, unknown>>;
};

/**
 * Zod schema for EntityComponentPatchOp. The `as z.ZodType<EntityComponentPatchOp>`
 * cast is needed for the same branded-type reason as EntityCreateOpSchema above.
 */
export const EntityComponentPatchOpSchema: z.ZodType<EntityComponentPatchOp> =
  z.object({
    op: z.literal("patch-entity-component"),
    entityId: z.union([z.string(), z.object({ $: z.string() })]),
    componentId: z.string().min(1),
    attrs: z.record(z.unknown()),
  }) as z.ZodType<EntityComponentPatchOp>;
